import { getUrl } from '../../config';
import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import axios from 'axios';
import ModalNotifications from "../linar/Modal/ModalNotifications";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatIcon from '@mui/icons-material/Chat';
import { useMessageNotifications } from '../../contexts/MessageNotificationsContext';
import useGlobalMessaging from '../../hooks/useGlobalMessaging';
import {
  Avatar as MuiAvatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover as MuiPopover,
  SvgIcon,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { Bell } from "react-feather";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 300px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const NotificationHeader = styled(Box)`
  text-align: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

function Notification({ title, description, Icon, type, onClick, isMessage = false, notificationData = null, onDelete = null }) {
  const [contextMenu, setContextMenu] = useState(null);

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick();
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (notificationData && notificationData.type === 'recrutement_created') {
      setContextMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleGoToRecrutement = () => {
    if (notificationData && notificationData.data && notificationData.data.recrutementId) {
      // Naviguer vers la page des recrutements avec l'ID du recrutement
      window.location.href = `/rh/recrutement?scrollTo=${notificationData.data.recrutementId}`;
    }
    handleCloseContextMenu();
  };

  const handleDeleteNotification = async () => {
    if (onDelete && notificationData) {
      await onDelete(notificationData.id);
    }
    handleCloseContextMenu();
  };

  return (
    <>
      <ListItem
        divider
        component={isMessage ? "div" : Link}
        to={isMessage ? undefined : "#"}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        sx={{
          cursor: 'pointer',
          ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        <ListItemAvatar sx={{ display: "flex", marginLeft: '5px' }}>
          <SvgIcon fontSize="small" sx={{
            backgroundColor: isMessage ? '#1976d2' : (type === 1 ? 'green' : 'red'),
            color: 'white',
            borderRadius: "100%",
            padding: "8px",
            width: "35px",
            height: "35px"
          }} >
            <Icon />
          </SvgIcon>
        </ListItemAvatar>
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            variant: "subtitle2",
            color: "textPrimary",
          }}
          secondary={description}
        />
      </ListItem>
      
      {/* Menu contextuel */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleGoToRecrutement}>
          Aller au recrutement
        </MenuItem>
        <MenuItem onClick={handleDeleteNotification} sx={{ color: 'error.main' }}>
          Supprimer la notification
        </MenuItem>
      </Menu>
    </>
  );
}

function NavbarNotificationsDropdown() {
  const ref = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { messageNotifications, markNotificationAsRead, removeNotificationsByConversation } = useMessageNotifications();
  const { socket } = useGlobalMessaging();

  const fetchNotifications = async () => {
    try {

      axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem("accessToken")}`;
      const response = await axios.post(getUrl() + `/api/notifications/getLast`);
      setNotifications(response.data.data.lignes);
    } catch (error) {
      console.log(error)
    }
  };

  // Naviguer vers une conversation
  const handleMessageNotificationClick = (messageNotification) => {
    // Supprimer toutes les notifications de cette conversation
    removeNotificationsByConversation(messageNotification.conversationId);
    setOpen(false);
    navigate('/gestion/messagerie', {
      state: { conversationId: messageNotification.conversationId }
    });
  };

  // Fonction pour supprimer une notification
  const deleteNotification = async (notificationId) => {
    try {
      axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem("accessToken")}`;
      await axios.delete(getUrl() + `/api/notifications/${notificationId}`);
      
      // Recharger les notifications
      await fetchNotifications();
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
    }
  };

  // Combiner les notifications système et de messages
  const allNotifications = [
    ...messageNotifications.slice(0, 2), // Max 2 notifications de messages
    ...notifications.slice(0, 2) // Max 2 notifications système
  ];



  React.useEffect(() => {
    fetchNotifications();
  }, []);

  // Écouter les nouvelles notifications via Socket.IO
  React.useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notificationData) => {
      console.log('Nouvelle notification reçue via Socket.IO:', notificationData);
      // Ajouter la nouvelle notification en haut de la liste
      setNotifications(prev => [notificationData, ...prev]);
    };

    socket.on('newNotification', handleNewNotification);

    return () => {
      socket.off('newNotification', handleNewNotification);
    };
  }, [socket]);

  const handleOpen = () => {
    setOpen(true);
    // Recharger les notifications quand on ouvre le dropdown
    fetchNotifications();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Indicator badgeContent={allNotifications.length}>
            <Bell />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <NotificationHeader p={2}>
          <Typography variant="subtitle1" color="textPrimary">
            {allNotifications.length} {t("New Notifications")}
          </Typography>
        </NotificationHeader>
        <List>
          {allNotifications.map((notification, index) => (
            <Box key={notification.id || index} sx={{ ":hover": { backgroundColor: '#29384f' } }}>
              <Notification
                title={notification.title}
                description={notification.description}
                Icon={
                  notification.type === 'message'
                    ? ChatIcon
                    : (notification.icon === 1 ? Bell : WarningAmberIcon)
                }
                type={notification.icon}
                isMessage={notification.type === 'message'}
                notificationData={notification}
                onDelete={deleteNotification}
                onClick={
                  notification.type === 'message'
                    ? () => handleMessageNotificationClick(notification)
                    : undefined
                }
              />
            </Box>
          ))}
        </List>
        {/* <Box p={1} display="flex" justifyContent="center">
          <ModalNotifications /> { }
        </Box> */}
      </Popover>
    </React.Fragment>
  );
}

export default NavbarNotificationsDropdown;
